/**
 * Workflow event (audit timeline) hooks — back the Flow Designer's merged
 * Comments tab timeline.
 *
 * Thin React Query wrappers over `/workflows/:id/events` and `/workflows/:id/
 * status`, modeled on `useWorkflowComments.ts`. The events list is newest-
 * first; `useSetWorkflowStatus` invalidates both the events list and the
 * workflow detail query (`['workflow-detail', workflowId]`) so the header
 * badge and the timeline refresh together.
 *
 * @module FlowDesigner/hooks/useWorkflowEvents
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { WorkflowManagementStatus } from '@chatsdet/types';
import { API_URL } from '../../../../config';
import { toApiError } from '../../../../lib/api-error';

export type WorkflowEventTicketRef = {
  id: string;
  issueId: string | null;
  externalUrl: string | null;
  externalProvider: string | null;
  externalIdentifier: string | null;
};

export type WorkflowEventType =
  | 'status_changed'
  | 'node_deactivated'
  | 'node_reactivated'
  | 'definition_changed'
  | 'maintenance_completed'
  | 'maintenance_gave_up'
  | 'maintenance_feedback_answered'
  | 'cache_edit_reconciled'
  | 'cache_pinned'
  | 'cache_unpinned';

export type WorkflowEvent = {
  id: string;
  workflowId: string;
  eventType: WorkflowEventType;
  fromStatus: WorkflowManagementStatus | null;
  toStatus: WorkflowManagementStatus | null;
  nodeId: string | null;
  nodeLabel: string | null;
  actorId: string;
  actorKind: 'user' | 'agent' | 'system';
  reason: string | null;
  revisionId: string | null;
  metadata: { revisionNumber?: number | null } | null;
  createdAt: string;
  tickets: WorkflowEventTicketRef[];
};

/** One ticket reference posted with a status change — internal issue XOR external URL. */
export type TicketInput =
  | { issueId: string }
  | { url: string; provider?: string; identifier?: string };

const eventsKey = (workflowId: string) => ['workflow', 'events', workflowId] as const;

export function useWorkflowEvents(workflowId: string | null | undefined) {
  return useQuery({
    queryKey: eventsKey(workflowId ?? ''),
    enabled: !!workflowId,
    queryFn: async () => {
      const r = await fetch(`${API_URL}/workflows/${workflowId}/events?limit=200`, {
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to load workflow events');
      const data = (await r.json()) as { ok: true; data: WorkflowEvent[] };
      return data.data;
    },
  });
}

export function useSetWorkflowStatus(workflowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      status: WorkflowManagementStatus;
      reason?: string;
      tickets?: TicketInput[];
    }) => {
      const r = await fetch(`${API_URL}/workflows/${workflowId}/status`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to update workflow status');
      return (await r.json()) as {
        ok: true;
        changed: boolean;
        workflow: unknown;
        event: WorkflowEvent | null;
      };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: eventsKey(workflowId) });
      void qc.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
    },
  });
}

/**
 * Deactivate/reactivate a node as a first-class audited event — replaces the
 * old client pattern of `PATCH node config` + a synthetic reason comment.
 */
export function useSetNodeDeactivated(workflowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      nodeId,
      deactivated,
      reason,
    }: {
      nodeId: string;
      deactivated: boolean;
      reason?: string;
    }) => {
      const r = await fetch(
        `${API_URL}/workflows/${workflowId}/nodes/${nodeId}/${deactivated ? 'deactivate' : 'activate'}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(reason ? { reason } : {}),
        }
      );
      if (!r.ok) throw await toApiError(r, 'Failed to update node activation');
      return (await r.json()) as {
        ok: true;
        changed: boolean;
        node: unknown;
        event: WorkflowEvent | null;
      };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: eventsKey(workflowId) });
      // FlowDesigner.tsx currently refetches the node list manually after this
      // mutation; invalidating here too is idempotent and keeps any other
      // consumer of this hook in sync without relying on that caller-side refetch.
      void qc.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
    },
  });
}
